import { Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { Nation, GovernmentType, ElectoralSystem, JudicialReviewStrength, MediaFreedom } from '@/types'

interface NationRulesCardProps {
  nation: Nation
}

const governmentLabels: Record<GovernmentType, string> = {
  parliamentary: 'Parliamentary',
  semi_presidential: 'Semi-Presidential',
  presidential: 'Presidential',
}

const governmentDescriptions: Record<GovernmentType, string> = {
  parliamentary: 'The head of government (Prime Minister) is elected by parliament. Government requires parliamentary confidence to remain in power.',
  semi_presidential: 'Both a President (directly elected) and Prime Minister (parliamentary) share executive power.',
  presidential: 'The President is directly elected and serves as both head of state and government, independent of parliament.',
}

const electoralLabels: Record<ElectoralSystem, string> = {
  nationwide_pr: 'Nationwide PR',
  regional_pr: 'Regional PR',
  mixed: 'Mixed System',
}

const electoralDescriptions: Record<ElectoralSystem, string> = {
  nationwide_pr: 'Seats are allocated proportionally based on nationwide vote share. All votes count equally regardless of location.',
  regional_pr: 'Seats are allocated proportionally within each region. Parties need support in specific regions to win seats there.',
  mixed: 'Combines regional constituency seats with proportional list seats. Some MPs represent districts, others come from party lists.',
}

const judicialLabels: Record<JudicialReviewStrength, string> = {
  none: 'None',
  weak: 'Weak',
  strong: 'Strong',
}

const judicialDescriptions: Record<JudicialReviewStrength, string> = {
  none: 'Courts cannot overturn legislation. Parliament has supreme authority over laws.',
  weak: 'Courts can review laws but parliament can override judicial decisions with a simple majority.',
  strong: 'Courts can strike down unconstitutional laws. Constitutional amendments require supermajorities.',
}

const mediaLabels: Record<MediaFreedom, string> = {
  free: 'Free',
  state_influenced: 'State Influenced',
  polarized: 'Polarized',
  censored: 'Censored',
}

const mediaDescriptions: Record<MediaFreedom, string> = {
  free: 'Independent media operates without government interference. Campaign messaging reaches voters directly.',
  state_influenced: 'State media has significant reach. Government messaging may affect public opinion.',
  polarized: 'Media is split along partisan lines. Voters tend to consume news that confirms their existing views.',
  censored: 'Government controls major media outlets. Opposition messaging is restricted.',
}

function formatTermLength(ticks: number): string {
  return `${ticks} ticks (${ticks} real days)`
}

function InfoTooltip({ content }: { content: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="text-muted-foreground hover:text-foreground inline h-3.5 w-3.5 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  )
}

export function NationRulesCard({ nation }: NationRulesCardProps) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Government Structure */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Government Structure</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Government Type
                <InfoTooltip content={governmentDescriptions[nation.government_type]} />
              </dt>
              <dd className="font-medium text-right">
                {governmentLabels[nation.government_type]}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Electoral System
                <InfoTooltip content={electoralDescriptions[nation.electoral_system]} />
              </dt>
              <dd className="font-medium text-right">
                {electoralLabels[nation.electoral_system]}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Term Length
                <InfoTooltip content="How long between elections. 1 tick = 1 real day = 3 in-game months." />
              </dt>
              <dd className="font-medium text-right">
                {formatTermLength(nation.term_length_ticks)}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Confidence Votes
                <InfoTooltip content="If enabled, parliament can vote to remove the government before the next election." />
              </dt>
              <dd className="font-medium text-right">
                {nation.confidence_votes_enabled ? 'Enabled' : 'Disabled'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Electoral Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Electoral Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Threshold
                <InfoTooltip content="Minimum vote share required to enter parliament. Parties below this threshold win no seats." />
              </dt>
              <dd className="font-medium text-right">{nation.threshold_percent}%</dd>
            </div>
            <div className="flex items-start justify-between gap-2">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Max Parties
                <InfoTooltip content="Maximum number of active parties allowed in this nation." />
              </dt>
              <dd className="font-medium text-right">{nation.max_parties}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Institutions - Full Width */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle className="text-lg">Institutions</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="flex items-start justify-between gap-2 sm:flex-col sm:justify-start">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Judicial Review
                <InfoTooltip content={judicialDescriptions[nation.judicial_review_strength]} />
              </dt>
              <dd className="font-medium sm:mt-1">
                {judicialLabels[nation.judicial_review_strength]}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-2 sm:flex-col sm:justify-start">
              <dt className="text-muted-foreground flex items-center gap-1.5">
                Media Freedom
                <InfoTooltip content={mediaDescriptions[nation.media_freedom]} />
              </dt>
              <dd className="font-medium sm:mt-1">{mediaLabels[nation.media_freedom]}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}
